import { useEffect, useState } from 'react'
import { api } from '../../shared/api/http'
import { ClientModelSelector, type ClientModelOption } from './ClientModelSelector'

type Target = { companyId: string; clientId: string; selectedModelId: string; updatedAtUtc: string }
type Preview = { selectedModelId: string; companyCount: number; targets: Target[] }
const endpoint = '/platform/administration/client-models/bulk'

export function BulkClientModels({ companyId, clientId, onApplied }: { companyId: string; clientId: string; onApplied: () => void }) {
  const [open, setOpen] = useState(false)
  const [models, setModels] = useState<ClientModelOption[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  useEffect(() => {
    if (!open) return
    let active = true
    setLoading(true); setError(''); setPreview(null); setSelected(null)
    api<ClientModelOption[]>(`/platform/administration/companies/${companyId}/clients/${clientId}/ai-models`)
      .then(value => { if (active) { const available = value.filter(m => m.id !== null); setModels(available); setSelected(available.find(m => m.ready)?.id ?? null) } })
      .catch(() => { if (active) setError('Não foi possível carregar os modelos.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [open, companyId, clientId])
  async function submit(apply: boolean) {
    if (busy || !selected) return
    setBusy(true); setError('')
    try {
      const result = await api<Preview>(`${endpoint}/${apply ? 'apply' : 'preview'}`, {
        method: 'POST', body: JSON.stringify({ selectedModelId: selected, targets: apply ? preview?.targets : undefined }),
      })
      if (apply) { setMessage(`${result.targets.length} clientes atualizados.`); setOpen(false); onApplied() }
      else setPreview(result)
    } catch {
      setPreview(null)
      setError('Atualização não aplicada. Confira se a versão está disponível e aceita os analíticos dos clientes. Se houve alteração nos clientes, consulte novamente.')
    } finally { setBusy(false) }
  }
  return <>
    <button type="button" className="compact-primary" disabled={!companyId || !clientId} onClick={() => { setMessage(''); setOpen(true) }}>Atualizar modelos em massa</button>
    {message && <p role="status">{message}</p>}
    {open && <div className="storage-modal storage-services-modal" role="dialog" aria-modal="true" aria-labelledby="bulk-model-title">
      <section style={{ width: 'min(590px, calc(100vw - 32px))', maxHeight: '90vh', overflow: 'auto', background: 'white', borderRadius: 24 }}>
        <header><div><strong id="bulk-model-title">Atualizar modelos em massa</strong><span>Todas as empresas</span></div><button type="button" disabled={busy} aria-label="Fechar atualização em massa" onClick={() => setOpen(false)}>×</button></header>
        <main style={{ padding: 20 }}>
          <p>Atualiza todos os clientes que usam versões deste modelo, em todas as empresas. Mantém analíticos, horários, regiões e demais configurações.</p>
          <ClientModelSelector models={models} selectedModelId={selected} loading={loading} error="" notice="" disabled={busy} onChange={id => { setSelected(id); setPreview(null); setError('') }} />
          {preview && <p role="status">{preview.targets.length} clientes em {preview.companyCount} empresas serão atualizados. Clientes que já usam esta versão permanecem como estão.</p>}
          {error && <p role="alert">{error}</p>}
        </main>
        <footer><button type="button" disabled={busy} onClick={() => setOpen(false)}>Cancelar</button><button type="button" className="compact-primary" disabled={busy || loading || !models.some(m => m.id === selected && m.ready) || preview?.targets.length === 0} onClick={() => void submit(!!preview)}>{busy ? 'Aguarde…' : preview ? 'Atualizar todos' : 'Consultar clientes afetados'}</button></footer>
      </section>
    </div>}
  </>
}
